import { PhoneMissed, FileX2, Users } from "lucide-react";
import { Eyebrow, Reveal } from "./utils";

const small = [
  { icon: PhoneMissed, title: "Missed Calls", copy: "The Customer May Not Call Twice." },
  { icon: FileX2, title: "Unfollowed Estimates", copy: "Interest Is Not the Same as a Booked Job." },
  { icon: Users, title: "Past Customers", copy: "Your Customer List Is an Untapped Growth Channel." },
];

export default function CostOfDelay() {
  return (
    <section data-testid="cost-of-delay-section" className="relative overflow-hidden bg-brand-black py-24 md:py-32">
      <div className="grain-overlay pointer-events-none absolute inset-0 opacity-[0.04]" />
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 h-[400px] w-[700px] rounded-full bg-brand-gold/10 blur-[140px]" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <Reveal><Eyebrow>The Cost of Delay</Eyebrow></Reveal>
          <Reveal delay={1}>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tight text-white leading-[1.08]">
              Your Next Revenue Opportunity May Already Be in Your <span className="gold-text">Inbox.</span>
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <div className="mt-7 space-y-4 font-sans text-base md:text-lg leading-relaxed text-white/60">
              <p>
                Before investing more money into lead generation, most service businesses should first understand what is
                happening to the leads, estimates, calls, and past customers they already have.
              </p>
              <p>
                A missed call, delayed response, untracked estimate, or inactive customer list can quietly become revenue
                for a competitor.
              </p>
            </div>
          </Reveal>
        </div>

        {/* Big 21x metric card */}
        <Reveal delay={1} className="mt-16">
          <div className="relative overflow-hidden rounded-3xl bg-brand-charcoal border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
            <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-brand-gold/15" />
            <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-brand-gold/15 blur-[100px]" />
            <div className="relative grid lg:grid-cols-[0.9fr_1.1fr] gap-8 lg:gap-12 p-9 md:p-14 items-center">
              <div className="flex items-center justify-center lg:justify-start">
                <span className="gold-text font-serif font-semibold leading-none text-[7rem] sm:text-[9rem] lg:text-[11rem] tracking-tight">
                  21×
                </span>
              </div>
              <div>
                <h3 className="font-serif text-3xl md:text-4xl tracking-tight text-white leading-tight">
                  Speed Changes What a Lead Is Worth.
                </h3>
                <p className="mt-5 font-sans text-base leading-relaxed text-white/60">
                  In a lead-response study, the odds of qualifying a web lead dropped sharply when first contact moved
                  from 5 minutes to 30 minutes.
                </p>
                <p className="mt-5 font-mono text-xs leading-relaxed text-white/35">
                  Source: MIT / InsideSales Lead Response Management Study. Results vary by business and sales process.
                </p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Three smaller cards */}
        <div className="mt-8 grid md:grid-cols-3 gap-6">
          {small.map((s, i) => (
            <Reveal key={s.title} delay={i}>
              <div className="group h-full rounded-2xl glass-dark p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand-gold/40">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-gold/12">
                  <s.icon className="h-6 w-6 text-brand-gold" strokeWidth={1.5} />
                </span>
                <h4 className="mt-6 font-sans text-lg font-semibold text-white">{s.title}</h4>
                <p className="mt-2 font-sans text-sm leading-relaxed text-white/55">{s.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
